import React, { useEffect, useRef, useState } from "react";
import { DropdownStatusList, EmptyString, FormTypeEdit, InsertDateFormat, PageSize, PageSizeOptions, PaymentMethodOptions, StatusCodeDelete } from "utils/Constant";
import TransactionReportFormModal from "./TransactionReportFormModal";
import Dropdown from "components/Input/Dropdown";
import { addSpaceEvery4, money, Truncate } from "utils/helpers";
import moment from "moment";
import DatePicker from "components/Input/DatePicker";
import FilterIcon from "assets/icon/FilterIcon";
import XIcon from "assets/icon/XIcon";
import ChevronDownIcon from "assets/icon/ChevronDownIcon";
import ChevronUpIcon from "assets/icon/ChevronUpIcon";
import { useModal } from "utils/context/ModalContext";
import { useCallApi } from "api/apiHooks";
import Pagination from "components/Input/PaginationButton";
import { FilterCard, GridDeleteButton, GridEditButton, MainBodyCard } from "components/Cards";
import Select from 'react-select'
import PrintButton from "components/Button/PrintButton";
import { TransactionReportService } from "reportServices/TransactionReportService";
import DataTable from "components/DataTable";
import PrimaryButton from "components/Button/PrimaryButton";
import { useVariable } from "utils/context/VariableContext";

export default function TransactionReport(){
    const { locationId, isAdmin, locationOptions } = useVariable()

    const { showFail, showLoading, hideLoading, showSuccess, confirm } = useModal()
    const { fetchTransactionList, fetchDeleteTransaction, fetchTransactionReportEdc } = useCallApi()

    const firstLoad = useRef(false)
    const debounceRef = useRef(null)

    const [transactionList, setTransactionList] = useState([])
    const [dateFrom, setDateFrom] = useState(new Date())
    const [dateTo, setDateTo] = useState(new Date())
    const [customerName, setCustomerName] = useState(EmptyString)
    const [cardNumber, setCardNumber] = useState(EmptyString)
    const [edcId, setEdcId] = useState(EmptyString)
    const [edcOptions, setEdcOptions] = useState([])
    const [status, setStatus] = useState(DropdownStatusList[1].value)
    const [pageSize, setPageSize] = useState(PageSizeOptions[0].value)
    const [isFilterOpen, setIsFilterOpen] = useState(false)
    const [isToggled, setIsToggled] = useState(false)
    const [isSummaryOpen, setIsSummaryOpen] = useState(false)
    const [pageIndex, setPageIndex] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [transactionReportFormModal, setTransactionReportFormModal] = useState({ isOpen: false, transactionId: 0, formType: FormTypeEdit, data: {} })

    const columns = [
        {
            key: "rowNumber",
            header: "No",
            headerClassName: "w-16 font-semibold",
            cellClassName: "text-center"
        },
        {
            key: "transactionDate",
            header: "Tanggal",
            render: (row) => moment(row.transactionDate).format('DD MMM YYYY'),
        },
        {
            key: "customerName",
            header: "Nama Customer",
            render: (row) => Truncate(row.customerName).toUpperCase(),
        },
        {
            key: "bankCode",
            header: "Bank",
            render: (row) => row.bankCode + ' - ' + row.vendorName,
        },
        {
            key: "cardNumber",
            header: "No Kartu",
            render: (row) => (
                <span className="text-nowrap">{addSpaceEvery4(row.cardNumber)}</span>
            ),
        },
        {
            key: "edcMdr",
            header: "MDR",
        },
        {
            key: "mdr",
            header: "Charge",
        },
        {
            key: "amount",
            header: "Gesek",
            render: (row) => money(String(Math.floor(row.amount))),
        },
        {
            key: "netto",
            header: "Terima",
            render: (row) => money(String(Math.floor(row.netto))),
        },
        {
            key: "profit",
            header: "Profit",
            render: (row) => money(String(Math.floor(row.profit))),
            visible: isAdmin
        },
        {
            key: "edcName",
            header: "EDC",
            render: (row) => row.edcBank + ' - ' + row.edcName.toUpperCase(),
        },
        {
            key: "reffNo",
            header: "Ref No",
        },
        {
            key: "paymentType",
            header: "Payment Type",
            render: (row) => row.paymentType !== '' ? PaymentMethodOptions.find(x => x.value === row.paymentType).label : '',
        },
        {
            key: "updateBy",
            header: "Update By",
        },
        {
            key: "action",
            header: "Action",
            render: (row) => (
                <div className="flex gap-2">
                    <GridEditButton isActive={isAdmin} onClick={() => editBtnClick(row.transactionId)} size="w-3" />
                    <GridDeleteButton isActive={row.status} onClick={() => deleteBtnClick(row.transactionId, row.status)} size="w-3" />
                </div>
            ),
        },
    ]
    
    async function handlePageChange(newIndex) {
        try {
            showLoading()

            const res = await fetchTransactionList(
                moment(dateFrom).format(InsertDateFormat),
                moment(dateTo).format(InsertDateFormat), 
                customerName,
                cardNumber,
                edcId === EmptyString ? 0 : edcId,
                status,
                pageSize === EmptyString ? PageSizeOptions[0].value : pageSize,
                parseInt(newIndex),
                EmptyString,
                locationId
            )

            if (!res) return

            const totalData = res?.data?.totalData ?? 0;
            const total = Math.max(1, Math.ceil(totalData / PageSize));

            setTransactionList(res)
            setTotalPages(total)
            setPageIndex(newIndex)

            // Get EDC
            const res2 = await fetchTransactionReportEdc(
                moment(dateFrom).format(InsertDateFormat),
                moment(dateTo).format(InsertDateFormat),
                status
            )

            if (!res2) return

            let data2 = res2.data
            let tempArray2 = []

            tempArray2.push({label: "All", value: EmptyString})

            for (let i = 0; i < data2.length; i++){
                tempArray2.push({ label: data2[i].bankCode + " - " + data2[i].edcName, value: String(data2[i].edcId) })
            }

            setEdcOptions(tempArray2)
            setEdcId(edcId)
        } finally {
            hideLoading()
        }
    }
 
    const filterBtnClick = () => {
        setIsFilterOpen(!isFilterOpen)
    }

    // Button Event Click
    const editBtnClick = (id) => {
        setTransactionReportFormModal({ isOpen: true, transactionId: id, formType: FormTypeEdit })
    }
    const deleteBtnClick = async (transactionId, status) => {
        const isConfirmed = await confirm()

        if (isConfirmed) {
            showLoading()

            const res = await fetchDeleteTransaction(transactionId, status)

            if (!res) return

            showSuccess()
            handlePageChange(pageIndex)
        }
    }
    const printBtnClick = async () => {
        const res = await fetchTransactionList(
            moment(dateFrom).format(InsertDateFormat),
            moment(dateTo).format(InsertDateFormat), 
            customerName,
            cardNumber,
            edcId === EmptyString ? 0 : edcId,
            status,
            100000,
            0,
            EmptyString,
            locationId
        )
        if (!res) return

        const data = res.data.listData
        
        TransactionReportService(
            data, 
            transactionList.data.totalAmount, 
            transactionList.data.totalNetto, 
            transactionList.data.totalProfit, 
            dateFrom, 
            dateTo, 
            locationOptions.find(x => x.value === locationId).label
        )
    }
    const previousDayClick = async () => {
        setDateFrom(moment(dateFrom).subtract(1, 'day'))
        setDateTo(moment(dateTo).subtract(1, 'day'))
    }

    const transactionFormModalClose = () => {
        setTransactionReportFormModal({ isOpen: false, transactionId: 0 })
        handlePageChange(pageIndex)
    }

    useEffect(() => {
        const onLoad = async () => {
            if (firstLoad.current) {
                handlePageChange(0)

                firstLoad.current = false
            }
            else {
                if (debounceRef.current) {
                    clearTimeout(debounceRef.current)
                }
        
                debounceRef.current = setTimeout(() => {
                    handlePageChange(0)
                }, 500)
            }
        }

        onLoad()
    },[dateFrom, dateTo, customerName, edcId, status, pageSize, isToggled, locationId])

    return(
        <MainBodyCard>
            <div>
                <span className="font-semibold text-xl sm:text-2xl">Transaction Report</span>
            </div>
            <hr />

            {/* Summary Mobile Size */}
            <div>
                <div className={`sm:hidden  px-3 bg-slate-100 flex justify-between items-center
                        ${isSummaryOpen ? 'border-t border-x rounded-t-xl pt-3 pb-1'  : 'py-3 border rounded-xl'}
                    `}
                    onClick={() => { setIsSummaryOpen(!isSummaryOpen) }}
                >
                    <div className="flex flex-col">
                        <span className="text-xs">Total Amount</span>
                        <span className="font-semibold">{"IDR " + money(String(Math.floor(transactionList?.data?.totalAmount)))}</span>
                    </div>
                    <div className="flex gap-2">
                        <div className="bg-black text-white px-3 py-1 rounded-full flex items-center">
                            <span className="text-xs">{transactionList?.data?.totalData} trx</span>
                        </div>
                        <div>
                            {isSummaryOpen ? <ChevronUpIcon className="fill-black w-6 h-6" /> : <ChevronDownIcon className="fill-black w-6 h-6" />}
                        </div>
                    </div>
                </div>

                {isSummaryOpen && 
                    <div className={`${isAdmin ? '' : 'hidden'}`}>
                        <div className="border-x py-1 px-3 bg-slate-100 flex justify-between items-center">
                            <div className="flex flex-col">
                                <span className="text-xs">Total Netto</span>
                                <span className="font-semibold">{"IDR " + money(String(Math.floor(transactionList?.data?.totalNetto)))}</span>
                            </div>
                        </div>
                        <div className="border-x border-b rounded-b-xl pt-1 pb-3 px-3 bg-slate-100 flex justify-between items-center">
                            <div className="flex flex-col">
                                <span className="text-xs">Profit</span>
                                <span className="font-semibold">{"IDR " + money(String(Math.floor(transactionList?.data?.totalProfit)))}</span>
                            </div>
                        </div>
                    </div>
                }
            </div>
            
            <div className="xl:hidden mt-2 flex justify-end">
                <button className="flex justify-center items-center gap-2 rounded-full border p-2 bg-black w-full" onClick={filterBtnClick}>
                    {isFilterOpen ? <XIcon className="fill-white w-6 h-6" /> : <FilterIcon className="fill-white w-6 h-6" />}
                    <span className="text-white text-sm">Filter</span>
                </button>
            </div>

            {/* FILTER */}
            <div className={`${isFilterOpen ? 'flex' : 'hidden'} xl:flex flex-col xl:flex-row gap-5 justify-between mt-2 bg-white sm:bg-transparent sm:p-0`}>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full flex-wrap text-sm sm:text-base">
                    <FilterCard title='Date From' width='w-full sm:w-56'>
                        <DatePicker className="w-full h-10 bg-white" value={dateFrom} onChange={setDateFrom} />
                    </FilterCard>

                    <FilterCard title='Date To' width='w-full sm:w-56'>
                        <DatePicker className="w-full h-10 bg-white" value={dateTo} onChange={setDateTo} />
                    </FilterCard>

                    <FilterCard title='' width='w-auto'>
                        <PrimaryButton title='H-1' onClick={previousDayClick} />
                    </FilterCard>

                    <FilterCard title='Customer Name' width='w-full sm:w-56'>
                        <input className="input" value={customerName} onChange={(e) => { setCustomerName(e.target.value.toUpperCase()) }} />
                    </FilterCard>
                    
                    <FilterCard title='EDC' width='w-full sm:w-56'>
                        <Select className="w-full" value={edcOptions.find(opt => opt.value === edcId)} options={edcOptions} onChange={(e) => { setEdcId(e.value) }} placeholder={"All"} />
                    </FilterCard>

                    <FilterCard title='Status' width='w-24'>
                        <Dropdown className='w-full' value={status} options={DropdownStatusList} onChange={(e) => { setStatus(e.value) }} placeholder={"All"} />
                    </FilterCard>
                </div>
            </div>

            <div className="flex items-center justify-between mt-3 text-sm">
                <div className="flex items-center gap-4">
                    <div>
                        <span className="">Show </span>
                    </div>
                    <div className="w-20">
                        <Dropdown className='' options={PageSizeOptions} value={pageSize} onChange={(e) => { setPageSize(e.value) }} />
                    </div>
                </div>

                <div>
                    {isAdmin && <PrintButton onClick={printBtnClick} />}
                </div>
            </div>
            
            {/* Wide Screen */}
            <div className="mt-3 w-full hidden xl:block">
                <DataTable
                    columns={columns}
                    data={transactionList?.data?.listData ?? []}
                    footer={
                        <tr className="h-10">
                            <td colSpan={8} className="text-right pr-2">{"Rp " + money(String(Math.floor(transactionList?.data?.totalAmount)))}</td>
                            <td className={`${isAdmin ? "text-nowrap" : "hidden"} px-2`}>{"Rp " + money(String(Math.floor(transactionList?.data?.totalNetto)))}</td>
                            <td colSpan={4} className={`${isAdmin ? "" : "hidden"} px-2`}>
                                {"Rp " + money(String(Math.floor(transactionList?.data?.totalProfit)))}
                            </td>
                            <td colSpan={2}>
                                <div className={`${isAdmin ? "" : "hidden"} bg-black mx-2 py-1 px-3 rounded`}>
                                    <span className="text-white">
                                        Margin : { (Math.trunc(transactionList?.data?.margin * 100) / 100).toFixed(2) } %
                                    </span>
                                </div>
                            </td>
                        </tr>
                    }
                />
            </div>

            {/* Mobile Size */}
            <div className="block xl:hidden mt-4">
                {transactionList?.data?.listData?.length > 0 ? 
                    <div className="flex flex-col gap-3">
                        {transactionList?.data?.listData?.map((data, i) => {
                            return(
                                <div className="flex flex-col bg-white border-b pb-3">
                                    <div className="flex w-full justify-between">
                                        <div className="flex flex-col">
                                            <div className="flex flex-col">
                                                <label className="text-sm">{data.customerName}</label>
                                                <label className="text-sm">{addSpaceEvery4(data.cardNumber)}</label>
                                                <label className="text-sm font-semibold">{"Rp " + money(String(Math.floor(data.amount)))}</label>
                                                <label className="text-sm font-semibold">{"Rp " + money(String(Math.floor(data.netto)))}</label>
                                            </div>
                                        </div>
                                        <div className="flex flex-col text-right">
                                            <label className="text-sm">{moment(data.transactionDate).format("DD MMM yyyy")}</label>
                                            <label className="text-sm">{data.mdr}</label>
                                            <label className={`text-sm font-semibold ${isAdmin ? '' : 'hidden'}`}>{"Rp " + money(String(Math.floor(data.profit)))}</label>
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <label className="text-sm">{data.edcBank + " - " + data.edcName}</label>
                                        <button className={`bg-black rounded-full px-2 py-1 flex gap-2`} onClick={() => deleteBtnClick(data.transactionId, data.status)}>
                                            <span className="text-xs text-white">{data.status === StatusCodeDelete ? 'Undelete' : 'Delete'}</span>
                                        </button>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                :
                    <div>
                        <span>No Transaction</span>
                    </div>
                }
            </div>

            <Pagination pageIndex={pageIndex} totalPages={totalPages} onPageChange={handlePageChange} />
            
            {transactionReportFormModal.isOpen && ( 
                <TransactionReportFormModal 
                    onClose={transactionFormModalClose} 
                    data={transactionReportFormModal?.data} 
                    transactionId={transactionReportFormModal?.transactionId} 
                    formType={transactionReportFormModal?.formType} 
                /> 
            )}
        </MainBodyCard>
    )
}